import React, { FC, useEffect, useMemo, useState } from 'react';
import { Col, Row, Form, Divider, Radio, Select, notification, Alert, Tooltip } from 'antd';
import type { FormInstance } from 'antd/es/form';
import { Modal, Input, Button } from '@hydrodigital/design-system';

import './modal.scss';
import { connect } from 'react-redux';
import { getUnitsAction } from '@/redux/slices/unitsSlice';
import { getTypesAction } from '@/redux/slices/measurementsSlice';
import {
	getTypesWithUnitsAction,
	selectUnitsByTypeId,
} from '@/redux/slices/measurementTypesWithUnitsSlice';
import InfrastructureService from '../../domain/Infrastructure/InfraestructureService';
import { message } from 'antd';
import { Parameter } from '@/domain/Infrastructure/Parameter';

interface IProps {
	types: any;
	units: any;
	typesWithUnits: any;
	loadUnits: any;
	isModalOpen: any;
	handleOk: any;
	handleCancel: any;
	handleGetInfrastructure: any;
	isNewParameter: any;
	editParameter: any;
	setIsModalOpen: any;
	infraId: any;
	infraType: any;
	parameters: any;
}

/**
 * @author
 * @function @AddInfrastructureModal
 **/

type LayoutType = Parameters<typeof Form>[0]['layout'];

const infrastructureService = new InfrastructureService();
function AddInfrastructureModal({
	types,
	units,
	typesWithUnits,
	loadUnits,
	isModalOpen,
	handleOk,
	handleCancel,
	handleGetInfrastructure,
	isNewParameter,
	editParameter,
	setIsModalOpen,
	infraId,
	infraType,
	parameters,
}: IProps) {
	const [formLayout, setFormLayout] = useState<LayoutType>('horizontal');
	const [form] = Form.useForm();
	const formRef = React.useRef<FormInstance>(null);
	const [errorMessage, setErrorMessage] = useState<any>('');
	const [unitsByParam, setUnitsByParam] = useState<any[]>([]);
	const selectedParamTypeId = Form.useWatch('paramTypeId', form);
	const shouldIncludeFallbackUnit =
		!isNewParameter &&
		`${selectedParamTypeId ?? editParameter?.paramTypeId ?? ''}` ===
			`${editParameter?.paramTypeId ?? ''}`;
	const currentParamUnitId = shouldIncludeFallbackUnit ? editParameter?.paramUnitId : null;
	const currentParamUnitInList = unitsByParam.some((u: any) => u.id === currentParamUnitId);
	const infraFallbackUnit =
		!currentParamUnitInList && currentParamUnitId != null
			? {
					id: currentParamUnitId,
					name: editParameter?.parameterUnit || `Unit ${currentParamUnitId}`,
				}
			: null;
	const usedTypeIds = useMemo(
		() =>
			new Set(
				(parameters || [])
					.map((parameter: any) => parameter?.paramTypeId)
					.filter((value: any) => value != null)
					.map((value: any) => String(value)),
			),
		[parameters],
	);
	const availableTypes = useMemo(
		() => types.filter((item: any) => !usedTypeIds.has(String(item?.id))),
		[types, usedTypeIds],
	);
	const defaultTypeId = useMemo(
		() => types.find((item: any) => item?.defaultV === true)?.id ?? null,
		[types],
	);

	const formItemLayout =
		formLayout === 'horizontal' ? { labelCol: { span: 15 }, wrapperCol: { span: 4 } } : null;

	// const [initialValues, setInitialValues] = useState(editParameter);

	const handleAddInfrastructureParemeters = async (values: Parameter) => {
		if (infraId) {
			return await infrastructureService.postAddParameter(infraId, values);
		}
	};

	function validateValues(values: any) {
		const { criticalHigh, criticalLow, criticalHhigh, criticalLlow } = values;

		if (criticalLlow > criticalLow) {
			return `Error: Critical Low Low (${criticalLlow}) must be less than or equal to Critical Low (${criticalLow}).`;
		}

		if (criticalLow >= criticalHigh) {
			return `Error: Critical Low (${criticalLow}) must be less than Critical High (${criticalHigh}).`;
		}

		if (criticalHigh > criticalHhigh) {
			return `Error: Critical High (${criticalHigh}) must be less than or equal to Critical High High (${criticalHhigh}).`;
		}
		return false;
	}

	const onFinish = async (values: Parameter) => {
		setErrorMessage(null);
		const error = validateValues(values);
		if (error) {
			setErrorMessage(error);
			return;
		}

		const response = await handleAddInfrastructureParemeters(values);
		if (response.success === 1) {
			form.resetFields();
			setIsModalOpen(false);
			await handleGetInfrastructure();
			notification.success({
				message: isNewParameter ? 'Parameter created' : 'Parameter edited',
				description: isNewParameter
					? 'Parameter created successfully'
					: 'Parameter edited successfully',
				placement: 'topRight',
			});
		}
		handleOk();
	};

	const onFinishFailed = (errorInfo: any) => {
		console.log('Failed:', errorInfo);
	};

	useEffect(() => {
		loadUnits && loadUnits(infraType);
	}, [infraType, loadUnits]);

	useEffect(() => {
		if (isNewParameter) {
			const fallbackType = availableTypes[0] ?? types[0] ?? null;
			const selectedDefaultType =
				defaultTypeId != null
					? (types.find((item: any) => String(item?.id) === String(defaultTypeId)) ?? fallbackType)
					: fallbackType;
			const selectedTypeId = selectedDefaultType?.id ?? null;
			const defaultUnits =
				selectedTypeId != null ? selectUnitsByTypeId(typesWithUnits, selectedTypeId) : [];

			// Default values
			form.setFieldsValue({
				criticalHigh: 1,
				criticalHhigh: 1,
				criticalLow: 0,
				criticalLlow: 0,
				samplerate: 5,
				txrate: 5,
				expirationTime: 100,
				paramUnitId: null,
				paramTypeId: selectedTypeId,
			});
			form.setFields([
				{ name: 'paramTypeId', errors: [] },
				{ name: 'paramUnitId', errors: [] },
			]);
			setUnitsByParam(defaultUnits);
		} else {
			// Edit parameter
			form.setFieldsValue({
				id: editParameter?.id,
				name: editParameter?.name,
				criticalHigh: editParameter?.highCritical,
				samplerate: editParameter?.sampleRate,
				criticalHhigh: editParameter?.hHighCritical,
				txrate: editParameter?.transferRate,
				criticalLow: editParameter?.lowCritical,
				expirationTime: editParameter?.expirationTime,
				criticalLlow: editParameter?.lLowCritical,
				principal: editParameter?.principal,
				paramUnitId: editParameter?.paramUnitId,
				paramTypeId: editParameter?.paramTypeId,
			});
			const units = selectUnitsByTypeId(typesWithUnits, editParameter?.paramTypeId);
			setUnitsByParam(units);
		}
	}, [availableTypes, defaultTypeId, editParameter, form, isNewParameter, types, typesWithUnits]);

	return (
		<Modal
			isOpen={isModalOpen}
			onClose={() => {
				form.resetFields();
				handleCancel();
			}}
			size="xl"
			closeOnOverlayClick={true}
			closeOnEscape={true}
			showCloseButton={false}
			testId="infrastructure-parameter-modal"
			// style={{
			//   width: '75%',
			// }}
		>
			{/* <Divider
        style={{
          marginLeft: "0px",
          width: "100%",
          border: "1px solid #E6E6E6",
        }}
      />
      {errorMessage && <Alert message={errorMessage} type="error" closable />} */}
			<Modal.Header>
				{/* <div className="title-modal">{!isNewParameter ? 'EDIT PARAMETER' : 'ADD PARAMETER'}</div>
				 */}
				<span className="text-title-page " style={{ fontSize: '1rem' }}>
					{!isNewParameter ? 'EDIT PARAMETER' : 'ADD PARAMETER'}
				</span>
				<Divider
					style={{
						marginLeft: '0px',
						width: '100%',
						border: '1px solid #E6E6E6',
					}}
				/>
			</Modal.Header>

			<Modal.Content>
				<div style={{ minWidth: '700px', marginLeft: 50 }}>
					{errorMessage && <Alert message={errorMessage} type="error" closable />}
					<Form
						form={form}
						{...formItemLayout}
						layout="vertical"
						onFinish={onFinish}
						onFinishFailed={onFinishFailed}
						autoComplete="off"
						size="small"
						style={{ maxWidth: 700 }}
					>
						<Form.Item name="id" hidden={true}>
							<Input />
						</Form.Item>
						<Form.Item
							name="name"
							rules={[
								{
									required: true,
									message: <span style={{ display: 'inline-block' }}>Required</span>,
								},
							]}
							label={<div className="item-label">Parameter name</div>}
						>
							<Input size="small" tabIndex={1} className="parameterClass" />
						</Form.Item>
						<Row gutter={16}>
							<Col className="gutter-row" span={6}>
								<div className="item-label jc-sb">
									<Tooltip title="Specify the critical high limit for the parameter. Values above this limit are considered critical.">
										<span>
											Critical<br></br> High
										</span>
									</Tooltip>
									<div style={{ marginLeft: 30 }}>
										<Form.Item
											name="criticalHigh"
											validateTrigger="onBlur"
											rules={[
												{
													validator: (_, value) => {
														if (value === undefined || value === '') return Promise.resolve();
														const num = Number(value);
														return Number.isFinite(num)
															? Promise.resolve()
															: Promise.reject(new Error('Only numbers'));
													},
												},
											]}
											getValueFromEvent={(e) => {
												const v = e?.target?.value;
												return v === '' ? undefined : Number(v);
											}}
										>
											<Input
												tabIndex={2}
												type="number"
												size="small"
												style={{ width: 62, height: 32 }}
												placeholder="0"
												className="numberClass"
											/>
										</Form.Item>
									</div>
								</div>

								<div className="item-label jc-sb">
									<Tooltip title="Specify the sampling rate (in minutes) at which this parameter will be measured.">
										<span>
											Sample<br></br> rate (min.)
										</span>
									</Tooltip>

									<div className="ml-3">
										<Form.Item
											name="samplerate"
											rules={[
												{
													required: true,
													message: (
														<span
															style={{
																display: 'inline-block',
															}}
														>
															Required
														</span>
													),
												},
												{
													validator: (_, value) =>
														value == undefined || value > 0
															? Promise.resolve()
															: Promise.reject(new Error('Only greater than 0 ')),
													message: (
														<span
															style={{
																display: 'inline-block',
															}}
														>
															Only greater than 0
														</span>
													),
												},
											]}
										>
											<Input
												tabIndex={6}
												type="number"
												size="small"
												style={{ width: 62, height: 32 }}
												placeholder="0"
												className="numberClass"
											/>
										</Form.Item>
									</div>
								</div>
							</Col>
							<Col className="gutter-row" span={6}>
								<div className="item-label jc-sb ml-2">
									<Tooltip title="Set an extreme high value for the parameter. This represents an extreme critical level.">
										<span>
											Critical<br></br> High High
										</span>
									</Tooltip>

									<div className="ml-5">
										<Form.Item
											name="criticalHhigh"
											validateTrigger="onBlur"
											rules={[
												{
													validator: (_, value) => {
														if (value === undefined || value === '') return Promise.resolve();
														const num = Number(value);
														return Number.isFinite(num)
															? Promise.resolve()
															: Promise.reject(new Error('Only numbers'));
													},
												},
											]}
											getValueFromEvent={(e) => {
												const v = e?.target?.value;
												return v === '' ? undefined : Number(v);
											}}
										>
											<Input
												tabIndex={3}
												type="number"
												size="small"
												style={{ width: 62, height: 32 }}
												placeholder="0"
												className="numberClass"
											/>
										</Form.Item>
									</div>
								</div>

								<div className="item-label jc-sb ml-2">
									<Tooltip title="Specify the transfer rate (in minutes) at which the data for this parameter is sent to this platform.">
										<span>
											Transfer<br></br> rate (min)
										</span>
									</Tooltip>

									<div className="ml-4">
										<Form.Item
											name="txrate"
											rules={[
												{
													required: true,
													message: (
														<span
															style={{
																display: 'inline-block',
															}}
														>
															Required
														</span>
													),
												},
												{
													validator: (_, value) =>
														value == undefined || value > 0
															? Promise.resolve()
															: Promise.reject(new Error('Only greater than 0 ')),
													message: (
														<span
															style={{
																display: 'inline-block',
															}}
														>
															Only greater than 0
														</span>
													),
												},
											]}
										>
											<Input
												tabIndex={7}
												type="number"
												size="small"
												style={{ width: 62, height: 32 }}
												placeholder="0"
												className="numberClass"
											/>
										</Form.Item>
									</div>
								</div>
							</Col>
							<Col className="gutter-row" span={6}>
								<div className="item-label jc-sb ml-3">
									<Tooltip title="Indicate the critical low limit for the parameter. Values below this limit are considered critical.">
										<span>
											Critical<br></br> Low
										</span>
									</Tooltip>

									<div className="ml-4">
										<Form.Item
											name="criticalLow"
											validateTrigger="onBlur"
											rules={[
												{
													validator: (_, value) => {
														if (value === undefined || value === '') return Promise.resolve();
														const num = Number(value);
														return Number.isFinite(num)
															? Promise.resolve()
															: Promise.reject(new Error('Only numbers'));
													},
												},
											]}
											getValueFromEvent={(e) => {
												const v = e?.target?.value;
												return v === '' ? undefined : Number(v);
											}}
										>
											<Input
												tabIndex={4}
												type="number"
												size="small"
												style={{ width: 62, height: 32 }}
												placeholder="0"
												className="numberClass"
											/>
										</Form.Item>
									</div>
								</div>

								<div className="item-label jc-sb ml-3">
									<Tooltip title="Specify the time (in minutes) after which the data for this parameter will be considered expired or invalid.">
										<span>
											Expiration <br></br> time (min)
										</span>
									</Tooltip>

									<div style={{ marginLeft: 15 }}>
										<Form.Item
											name="expirationTime"
											rules={[
												{
													required: true,
													message: (
														<span
															style={{
																display: 'inline-block',
															}}
														>
															Required
														</span>
													),
												},
												{
													validator: (_, value) =>
														value == undefined || value > 0
															? Promise.resolve()
															: Promise.reject(new Error('Only greater than 0 ')),
													message: (
														<span
															style={{
																display: 'inline-block',
															}}
														>
															Only greater than 0
														</span>
													),
												},
											]}
										>
											<Input
												tabIndex={8}
												type="number"
												size="small"
												style={{ width: 62, height: 32 }}
												placeholder="0"
												className="numberClass"
											/>
										</Form.Item>
									</div>
								</div>
							</Col>
							<Col className="gutter-row" span={6}>
								<div className="item-label jc-sb ml-4">
									<Tooltip title="Define an extreme low value for the parameter. This represents an extreme critical level.">
										<span>
											Critical<br></br> Low Low
										</span>
									</Tooltip>

									<div className="ml-3">
										<Form.Item
											name="criticalLlow"
											validateTrigger="onBlur"
											rules={[
												{
													validator: (_, value) => {
														if (value === undefined || value === '') return Promise.resolve();
														const num = Number(value);
														return Number.isFinite(num)
															? Promise.resolve()
															: Promise.reject(new Error('Only numbers'));
													},
												},
											]}
											getValueFromEvent={(e) => {
												const v = e?.target?.value;
												return v === '' ? undefined : Number(v);
											}}
										>
											<Input
												tabIndex={5}
												type="number"
												size="small"
												style={{ width: 62, height: 32 }}
												placeholder="0"
												className="numberClass"
											/>
										</Form.Item>
									</div>
								</div>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col className="gutter-row" span={12}>
								<div className="item-label jc-fs">
									<span>Type</span>
									<div className="ml-5">
										<Tooltip
											title={
												!isNewParameter
													? "Parameter types can't be changed after creation. To use a different type, delete this parameter and create a new one"
													: ''
											}
										>
											<div>
												<Form.Item
													wrapperCol={{
														xs: {
															span: 24,
															offset: 0,
														},
														sm: {
															span: 24,
															offset: 0,
														},
													}}
													name={'paramTypeId'}
													rules={[
														{
															required: true,
															message: (
																<span
																	style={{
																		display: 'inline-block',
																	}}
																>
																	Required
																</span>
															),
														},
														{
															validator: (_, value) =>
																value !== 0
																	? Promise.resolve()
																	: Promise.reject(new Error('Should select an option')),
														},
														{
															validator: (_, value) =>
																value == null
																	? Promise.resolve()
																	: isNewParameter &&
																		  defaultTypeId != null &&
																		  String(value) === String(defaultTypeId)
																		? Promise.resolve()
																		: (parameters || []).findIndex(
																					(p: any) =>
																						String(p?.paramTypeId) === String(value) &&
																						(isNewParameter ||
																							String(p?.id) !== String(editParameter?.id)),
																			  ) >= 0
																			? Promise.reject(new Error('Type is already in use'))
																			: Promise.resolve(),
														},
													]}
													style={{ width: '100%' }}
												>
													<Select
														tabIndex={12}
														placeholder="Select an option"
														style={{ minWidth: 140, maxWidth: 150 }}
														disabled={!isNewParameter}
														fieldNames={{ label: 'name', value: 'id' }}
														options={types}
														optionRender={({ data, label }) => (
															<Tooltip
																title={data?.description ?? 'No description'}
																styles={{ body: { whiteSpace: 'pre-wrap', maxWidth: 520 } }}
															>
																<div>{label}</div>
															</Tooltip>
														)}
														onChange={(value: number) => {
															setUnitsByParam([]);
															const units = selectUnitsByTypeId(typesWithUnits, value);
															setUnitsByParam(units);
															form.setFieldValue('paramUnitId', undefined);
															form.setFields([
																{ name: 'paramTypeId', errors: [] },
																{ name: 'paramUnitId', errors: [] },
															]);
														}}
													/>
												</Form.Item>
											</div>
										</Tooltip>
									</div>
								</div>
							</Col>
							<Col className="gutter-row" span={12}>
								<div className="item-label jc-fs ml-3">
									<span>Display Unit</span>
									<div className="ml-6">
										<Tooltip
											title={
												!isNewParameter
													? "Parameter types can't be changed after creation. To use a different type, delete this parameter and create a new one"
													: ''
											}
										>
											<div>
												<Form.Item
													name={'paramUnitId'}
													rules={[
														{
															required: true,
															message: (
																<span
																	style={{
																		display: 'inline-block',
																	}}
																>
																	Required
																</span>
															),
														},
														{
															validator: (_, value) =>
																value !== 0
																	? Promise.resolve()
																	: Promise.reject(new Error('Should select an option')),
														},
													]}
													wrapperCol={{
														xs: {
															span: 24,
															offset: 0,
														},
														sm: {
															span: 24,
															offset: 0,
														},
													}}
												>
													<Select
														tabIndex={13}
														placeholder="Select an option"
														style={{ minWidth: 120, maxWidth: 150 }}
														disabled={!isNewParameter}
														options={[
															...(infraFallbackUnit
																? [{ value: infraFallbackUnit.id, label: infraFallbackUnit.name }]
																: []),
															...unitsByParam.map((unit: { id: any; name: any }) => {
																return { value: unit.id, label: unit.name };
															}),
														]}
													/>
												</Form.Item>
											</div>
										</Tooltip>
									</div>
								</div>
							</Col>
							<div className="item-label mt-3">
								<span>Select as main parameter?</span>
								<div style={{ marginTop: -10, marginLeft: 10 }}>
									<Form.Item
										name={'principal'}
										rules={[
											{
												required: true,
												message: (
													<span
														style={{
															display: 'inline-block',
														}}
													>
														Required
													</span>
												),
											},
										]}
									>
										<Radio.Group className="item-label">
											<Radio value={true} tabIndex={14}>
												<div className="item-label">Yes</div>
											</Radio>
											<Radio value={false} tabIndex={15}>
												<div className="item-label">No</div>
											</Radio>
										</Radio.Group>
									</Form.Item>
								</div>
							</div>
						</Row>
						<Divider
							style={{
								marginLeft: '-15px',
								marginRight: '15px',
								width: '800px',
								border: '1px solid #E6E6E6',
							}}
						/>

						<div className="flex jc-fe-extra">
							<Button
								label="Cancel"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#F7F9FA',
									color: '#434B56',
									border: '1px solid #C4CCD5',
								}}
								onClick={handleCancel}
							/>

							<div style={{ marginLeft: 20 }}>
								<Button
									label="Accept"
									showIcon={false}
									onClick={() => form.submit()}
									style={{
										width: '85px',
										height: '40px',
										backgroundColor: '#00203C',
										color: 'white',
									}}
									onKeyDown={(event) => {
										if (event.key === 'Tab') {
											event.preventDefault();
											const firstElement: any = document.querySelector('[tabindex="1"]');
											if (firstElement) {
												firstElement.focus();
											}
										}
									}}
								/>
							</div>
						</div>
					</Form>
				</div>
			</Modal.Content>
		</Modal>
	);
}

function mapState(state: {
	types: { array: any };
	units: { array: any };
	typesWithUnits: { array: any };
}) {
	return {
		types: state.types.array,
		units: state.units.array,
		typesWithUnits: state.typesWithUnits.array,
	};
}

const mapDispatch = (dispatch: any) => {
	return {
		loadUnits: (infraType?: number | string) => {
			dispatch(getUnitsAction());
			dispatch(getTypesAction(infraType));
			dispatch(getTypesWithUnitsAction());
		},
	};
};

export default connect(mapState, mapDispatch)(AddInfrastructureModal);
